import React, { useMemo, useState } from 'react';
import type { PostureAnalysis } from '@/lib/types';
import { getYoutubeVideoId } from '@/components/drill-library/services/youtube';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';
import { Activity, CheckCircle2, ExternalLink, Info, Camera } from 'lucide-react';
import { ClassificationDetails } from '@/components/posture-analysis/ClassificationDetails';
import { buildSelfAssessmentReportSections, FHP_LABEL } from './self-assessment-report-content';
import { formatDateLocale, cn } from '@/lib/utils';
import { AnnotatedImage } from '@/components/posture-analysis/AnnotatedImage';
import AnalysisPanels from '@/components/posture-analysis/AnalysisPanels';
import type { CaptureSequence } from '@/lib/types';

function DrillVideoBlock({
  drill,
  isPdf,
  followAppTheme,
}: {
  drill: { name: string; videoUrl?: string };
  isPdf?: boolean;
  followAppTheme?: boolean;
}) {
  const videoId = getYoutubeVideoId(drill.videoUrl);
  if (!videoId) return null;

  if (isPdf) {
    const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    const fallbackThumbnailUrl = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    const lastResortThumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

    const handleImgError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const target = e.currentTarget;
      if (target.src.includes('maxresdefault.jpg')) {
        target.src = fallbackThumbnailUrl;
      } else if (target.src.includes('mqdefault.jpg')) {
        target.src = lastResortThumbnailUrl;
        target.classList.add('scale-[1.335]');
      }
    };

    return (
      <div className="mt-3 w-full max-w-md overflow-hidden rounded-xl border border-[#ecf0f1] shadow-sm relative bg-[#161e2a]">
        {drill.videoUrl ? (
          <a
            href={drill.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block relative w-full aspect-video overflow-hidden"
            data-testid={`drill-preview-link-${videoId}`}
          >
            <img
              src={thumbnailUrl}
              onError={handleImgError}
              alt={drill.name}
              className="w-full h-full object-cover transition-transform duration-300"
              crossOrigin="anonymous"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/25">
              <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center shadow-lg">
                <div className="w-0 h-0 border-y-[7px] border-y-transparent border-l-[12px] border-l-white ml-1" />
              </div>
            </div>
          </a>
        ) : (
          <div className="relative w-full aspect-video overflow-hidden">
            <img
              src={thumbnailUrl}
              onError={handleImgError}
              alt={drill.name}
              className="w-full h-full object-cover transition-transform duration-300"
              crossOrigin="anonymous"
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'mt-4 w-full max-w-md overflow-hidden rounded-xl shadow-sm',
        followAppTheme ? 'border border-line' : 'border border-[#ecf0f1]',
      )}
    >
      <LiteYouTubeEmbed id={videoId} title={drill.name} poster="maxresdefault" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface SelfAssessmentReportProps {
  analysis: PostureAnalysis;
  /** When true, show Analysis Details (after PDF download). */
  detailsUnlocked?: boolean;
  /** Download CTA or post-download confirmation — shown after summary cards. */
  downloadSection?: React.ReactNode;
  /** Screen (default) or PDF capture variant with hex colors and no gate UI. */
  variant?: 'screen' | 'pdf';
  /**
   * When true, screen chrome follows app theme tokens (sales/admin).
   * Default stays the branded light palette used by self-assessment and PDF export.
   */
  followAppTheme?: boolean;
  /** Shown on PDF variant under the subtitle. */
  contactName?: string;
  /** When false, include exercise recommendations. Defaults to true (hidden) for self-assessment and simple-assessment reports. */
  hideExercises?: boolean;
  /** Optional Conor Harris Video Section for screen mode. */
  videoSection?: React.ReactNode;
  /** Optional Lead Gate Form for Option 1. */
  leadGateSection?: React.ReactNode;
  /** Optional Calendly Booking Widget Section. */
  calendlySection?: React.ReactNode;
  /** Callback fired when missing landmarks are dynamically detected client-side. */
  onLandmarksDetected?: (view: CaptureSequence, landmarks: Landmark[], cmPerPx: number) => void;
}

/**
 * SelfAssessmentReport - High-fidelity posture report.
 * Default screen + PDF keep the branded light palette (public self-assessment export).
 * Pass followAppTheme for in-app surfaces such as /sales/prospects.
 */
const SelfAssessmentReport: React.FC<SelfAssessmentReportProps> = ({
  analysis,
  detailsUnlocked = false,
  downloadSection,
  variant = 'screen',
  followAppTheme = false,
  contactName,
  hideExercises,
  videoSection,
  leadGateSection,
  calendlySection,
  onLandmarksDetected,
}) => {
  const [activeView, setActiveView] = useState<CaptureSequence>('Anterior');
  const isPdf = variant === 'pdf';
  const useAppTheme = !isPdf && followAppTheme;
  const shouldHideExercises = hideExercises ?? true;
  const { orientation: orientationInfo, lateral: lateralInfo, layman, classification, drills, exercisesIntro, disclaimer } =
    useMemo(
      () => buildSelfAssessmentReportSections(analysis, { hideExercises: shouldHideExercises }),
      [analysis, shouldHideExercises],
    );

  const activeLateralClues = (layman?.lateralReasoning ?? []).filter(
    (reason) =>
      reason !== 'Your weight looks evenly balanced left to right' &&
      reason !== 'We need a front or back photo to check left–right balance'
  );

  const hasFhp = classification?.signals?.some(
    (sig: string) => sig.toLowerCase().startsWith('forward head')
  ) || false;

  const patternLower = classification?.pattern?.toLowerCase() || '';
  const isCompensation = patternLower && !patternLower.includes('stack') && !patternLower.includes('neutral');

  const brandedAccent = isPdf ? '#8e44ad' : 'oklch(52% 0.18 305)';
  const brandedAccentDark = isPdf ? '#7d3c98' : 'oklch(42% 0.16 305)';

  const brandedStyles = {
    container: `flex flex-col max-w-3xl mx-auto light ${isPdf ? 'gap-4 py-2 px-1 bg-[#f9f9f9]' : 'gap-8 py-6 px-1'}`,
    header: 'flex flex-col gap-2 mb-2',
    title: "text-3xl font-bold tracking-tight text-[#161e2a] font-['Poppins',sans-serif]",
    subtitle: "text-base text-[#595959] font-['Roboto',sans-serif] leading-relaxed",
    card: `bg-[#ffffff] border border-[#ecf0f1] rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.06)] flex flex-col ${isPdf ? 'p-4 gap-3' : 'p-6 gap-4'}`,
    cardTitle: "text-xl font-bold text-[#161e2a] font-['Poppins',sans-serif] flex items-center gap-2",
    cardText: "text-[16px] leading-relaxed text-[#595959] font-['Roboto',sans-serif]",
    badge: 'inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider leading-none',
    drillCard: 'flex flex-col items-center gap-4 text-center py-4 border-t border-[#ecf0f1] first:border-t-0 first:pt-0',
    drillName: "text-lg font-bold text-[#161e2a] font-['Poppins',sans-serif]",
    drillDesc: "text-sm text-[#595959] font-['Roboto',sans-serif] leading-relaxed max-w-prose",
    iconContainer: 'p-2.5 rounded-xl w-fit',
    neutralBadge: 'bg-[#f9f9f9] text-[#595959] border border-[#ecf0f1]',
    nestedWell: 'bg-[#f9f9f9] rounded-xl border border-[#ecf0f1] p-1',
    observationsRule: 'mt-2 pt-3 border-t border-[#ecf0f1]/60 flex flex-col gap-1.5',
    observationsTitle: "text-xs font-bold uppercase tracking-wider text-[#161e2a] font-['Roboto',sans-serif]",
    observationsItem: "text-sm text-[#595959] font-['Roboto',sans-serif]",
    dateLine: "text-sm text-[#595959] font-['Roboto',sans-serif]",
    analysisHint: "text-sm text-[#595959] -mt-2 mb-2 font-['Roboto',sans-serif]",
    exercisesIntro: "text-sm text-[#595959] mt-2 font-['Roboto',sans-serif]",
    videoLink: 'inline-flex items-center gap-1.5 text-xs font-bold hover:underline mt-1.5 px-3.5 py-2 rounded-xl border border-[#ecf0f1] bg-[#f9f9f9]',
    disclaimer: "text-xs text-[#595959] leading-relaxed font-['Roboto',sans-serif]",
    disclaimerWrap: 'mt-2 pt-4 border-t border-[#ecf0f1]',
    screenDisclaimerWrap: 'mt-4 pt-6 border-t border-[#ecf0f1]',
    accentBadge: undefined as string | undefined,
    iconTint: undefined as string | undefined,
    drillCheck: undefined as string | undefined,
  };

  const themedStyles = {
    container: 'flex flex-col max-w-3xl mx-auto gap-8 py-6 px-1',
    header: 'flex flex-col gap-2 mb-2',
    title: "text-3xl font-bold tracking-tight text-fg font-['Poppins',sans-serif]",
    subtitle: "text-base text-muted font-['Roboto',sans-serif] leading-relaxed",
    card: 'inner-panel bg-surface border border-line rounded-2xl shadow-sm flex flex-col p-6 gap-4',
    cardTitle: "text-xl font-bold text-fg font-['Poppins',sans-serif] flex items-center gap-2",
    cardText: "text-[16px] leading-relaxed text-muted font-['Roboto',sans-serif]",
    badge: 'inline-flex items-center justify-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider leading-none',
    drillCard: 'flex flex-col items-center gap-4 text-center py-4 border-t border-line first:border-t-0 first:pt-0',
    drillName: "text-lg font-bold text-fg font-['Poppins',sans-serif]",
    drillDesc: "text-sm text-muted font-['Roboto',sans-serif] leading-relaxed max-w-prose",
    iconContainer: 'p-2.5 rounded-xl w-fit bg-primary/10 text-primary',
    neutralBadge: 'bg-bg text-muted border border-line',
    nestedWell: 'nested-surface rounded-xl border border-line p-1',
    observationsRule: 'mt-2 pt-3 border-t border-line flex flex-col gap-1.5',
    observationsTitle: "text-xs font-bold uppercase tracking-wider text-fg font-['Roboto',sans-serif]",
    observationsItem: "text-sm text-muted font-['Roboto',sans-serif]",
    dateLine: "text-sm text-muted font-['Roboto',sans-serif]",
    analysisHint: "text-sm text-muted -mt-2 mb-2 font-['Roboto',sans-serif]",
    exercisesIntro: "text-sm text-muted mt-2 font-['Roboto',sans-serif]",
    videoLink: 'inline-flex items-center gap-1.5 text-xs font-bold hover:underline mt-1.5 px-3.5 py-2 rounded-xl border border-line bg-bg text-primary',
    disclaimer: "text-xs text-muted leading-relaxed font-['Roboto',sans-serif]",
    disclaimerWrap: 'mt-2 pt-4 border-t border-line',
    screenDisclaimerWrap: 'mt-4 pt-6 border-t border-line',
    accentBadge: 'bg-primary/10 text-primary border border-primary/40',
    iconTint: 'text-primary',
    drillCheck: 'bg-success text-success-foreground/10 text-success-text',
  };

  const styles = useAppTheme ? themedStyles : brandedStyles;

  const accentBadgeStyle: React.CSSProperties | undefined = useAppTheme
    ? undefined
    : {
        backgroundColor: isPdf ? 'rgba(142, 68, 173, 0.12)' : 'oklch(52% 0.18 305 / 0.12)',
        color: brandedAccentDark,
        border: `1px solid ${isPdf ? 'rgba(142, 68, 173, 0.4)' : 'oklch(52% 0.18 305 / 0.4)'}`,
      };

  const iconContainerStyle: React.CSSProperties | undefined = useAppTheme
    ? undefined
    : {
        backgroundColor: isPdf ? 'rgba(142, 68, 173, 0.08)' : 'oklch(52% 0.18 305 / 0.08)',
        color: brandedAccent,
      };

  const iconStyle: React.CSSProperties | undefined = useAppTheme ? undefined : { color: brandedAccent };

  const drillCheckStyle: React.CSSProperties | undefined = useAppTheme
    ? undefined
    : {
        backgroundColor: isPdf ? 'rgba(34, 197, 94, 0.1)' : 'oklch(58% 0.16 145 / 0.1)',
        color: isPdf ? '#15803d' : 'oklch(38% 0.12 145)',
      };

  const linkStyle: React.CSSProperties | undefined = useAppTheme ? undefined : { color: brandedAccent };

  const reportDate =
    analysis.createdAt && typeof (analysis.createdAt as { toDate?: () => Date }).toDate === 'function'
      ? (analysis.createdAt as { toDate: () => Date }).toDate()
      : analysis.createdAt instanceof Date
        ? analysis.createdAt
        : new Date();

  return (
    <div className={styles.container} data-testid="self-assessment-report-pdf-root">
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@700&family=Roboto:wght@400;700&display=swap" rel="stylesheet" />

      <div className={styles.header}>
        <div className={styles.iconContainer} style={iconContainerStyle}>
          <Activity className="h-6 w-6" />
        </div>
        <h1 className={styles.title}>Your Posture Report</h1>
        <p className={styles.subtitle}>
          Based on your 3-view posture assessment and Conor Harris's Biomechanics Methodology.
        </p>
        {isPdf && contactName?.trim() && (
          <p className={styles.subtitle}>Prepared for {contactName.trim()}</p>
        )}
        {isPdf && (
          <p className={styles.dateLine}>
            Date: {formatDateLocale(reportDate)}
          </p>
        )}
      </div>

      {/* 1 & 2. Book Call Text & Calendly Widget Section */}
      {!isPdf && calendlySection}

      {/* 3. Visual Posture Analysis (Your Captured Photos with Skeletons) */}
      {!isPdf && (
        <div
          className={cn(
            'flex flex-col items-center gap-4 w-full rounded-2xl p-6',
            useAppTheme
              ? 'inner-panel bg-surface border border-line shadow-sm'
              : 'bg-white border border-[#ecf0f1] shadow-[0_4px_24px_rgba(0,0,0,0.06)]',
          )}
        >
          <div className="text-left w-full">
            <h3
              className={cn(
                "text-lg font-bold font-['Poppins',sans-serif] flex items-center gap-2",
                useAppTheme ? 'text-fg' : 'text-[#161e2a]',
              )}
            >
              <Camera className={cn('h-5 w-5', useAppTheme && 'text-primary')} style={iconStyle} />
              Your Captured Posture Photos
            </h3>
            <p
              className={cn(
                "text-xs mt-1 font-['Roboto',sans-serif]",
                useAppTheme ? 'text-muted' : 'text-[#595959]',
              )}
            >
              Click on a view to see the joint landmarks overlay detected by our AI.
            </p>
          </div>

          {/* View Selector Tabs */}
          <div
            className={cn(
              'flex rounded-lg p-1 gap-1 w-full max-w-md',
              useAppTheme ? 'border border-line bg-bg' : 'border border-[#ecf0f1] bg-[#f9f9f9]',
            )}
          >
            {(['Anterior', 'Left', 'Right'] as const).map((view) => {
              const label = view === 'Anterior' ? 'Front' : view === 'Left' ? 'Left Side' : 'Right Side';
              const isActive = activeView === view;
              const hasPhoto = !!analysis.captured[view];

              return (
                <button
                  key={view}
                  type="button"
                  disabled={!hasPhoto}
                  onClick={() => setActiveView(view)}
                  className={cn(
                    "flex-1 py-2 text-xs font-bold rounded-md transition-all select-none font-['Roboto',sans-serif]",
                    isActive
                      ? useAppTheme
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'bg-[oklch(52%_0.18_305)] text-white shadow-sm'
                      : useAppTheme
                        ? 'text-muted hover:text-fg disabled:opacity-40'
                        : 'text-[#595959] hover:text-[#161e2a] disabled:opacity-40',
                  )}
                >
                  {label}
                  {!hasPhoto && " (No Photo)"}
                </button>
              );
            })}
          </div>

          {/* Large Image Preview Card */}
          <div
            className={cn(
              'w-full flex justify-center max-w-sm rounded-xl p-2',
              useAppTheme ? 'nested-surface border border-line' : 'bg-[#faf9f8] border border-[#ecf0f1]',
            )}
          >
            {analysis.captured[activeView] ? (
              <AnnotatedImage
                key={activeView}
                view={activeView}
                analysis={analysis}
                onLandmarksDetected={onLandmarksDetected}
              />
            ) : (
              <div
                className={cn(
                  "flex items-center justify-center text-sm font-['Roboto',sans-serif]",
                  useAppTheme ? 'text-muted' : 'text-muted-foreground',
                )}
              >
                Awaiting photo upload
              </div>
            )}
          </div>

          {/* Active View Angle Measurements (Head tilt, Shoulders level, Knee state, Ankle position, etc.) */}
          {analysis.captured[activeView] && (
            <div className="w-full max-w-md mt-2">
              <AnalysisPanels
                analysisData={analysis.analysisData || {}}
                model3D={null}
                capturedImages={analysis.captured}
                settings={{
                  postureOrientationStrategyId: 'auto',
                  units: 'metric',
                }}
                currentView={activeView}
                variant="metricsOnly"
                suppressViewTitle={false}
                experimentalFeaturesEnabled={false}
                metricsDensity="compact"
              />
            </div>
          )}
        </div>
      )}

      {/* 4. Orientation and Left-Right (Global Pattern & Side Balance Cards) */}
      <div className={isPdf ? 'flex flex-row [&>*]:flex-1 [&>*]:px-3 -mx-3' : 'grid grid-cols-1 md:grid-cols-2 gap-6'}>
        <div className={isPdf ? 'flex flex-col gap-4' : 'flex flex-col gap-6'}>
          {orientationInfo && (
            <div className={cn(styles.card, isPdf && 'mb-4')}>
              <div className={styles.cardTitle}>
                <Info className={cn('h-5 w-5', styles.iconTint)} style={iconStyle} />
                Global Pattern
              </div>
              <div className={cn(styles.badge, 'w-fit', styles.accentBadge)} style={accentBadgeStyle}>
                {orientationInfo.label}
              </div>
              <p className={styles.cardText}>{orientationInfo.explanation}</p>
            </div>
          )}

          {hasFhp && isCompensation && (
            <div className={styles.card}>
              <div className={styles.cardTitle}>
                <Info className={cn('h-5 w-5', styles.iconTint)} style={iconStyle} />
                Forward Head Posture
              </div>
              <div className={`${styles.badge} ${styles.neutralBadge} w-fit`}>
                Forward Head Posture
              </div>
              <p className={styles.cardText}>
                {FHP_LABEL.explanation}
              </p>
            </div>
          )}
        </div>

        {lateralInfo && (
          <div className={styles.card}>
            <div className={styles.cardTitle}>
              <Info className={cn('h-5 w-5', styles.iconTint)} style={iconStyle} />
              Side Balance
            </div>
            <div className={`${styles.badge} ${styles.neutralBadge} w-fit`}>
              {lateralInfo.label}
            </div>
            <p className={styles.cardText}>{lateralInfo.explanation}</p>
            {activeLateralClues.length > 0 && (
              <div className={styles.observationsRule}>
                <span className={styles.observationsTitle}>
                  Specific Observations:
                </span>
                <ul className="list-disc pl-4 flex flex-col gap-1">
                  {activeLateralClues.map((clue, idx) => (
                    <li key={idx} className={styles.observationsItem}>
                      {clue}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 5. Angle Results (Analysis Details & Measurements) */}
      {!isPdf && !detailsUnlocked && !leadGateSection && (
        <div className={styles.card}>
          <p className={`${styles.cardText} text-center`}>
            {shouldHideExercises
              ? 'Download your report to unlock the full analysis breakdown tailored to your posture pattern.'
              : 'Download your report to unlock the full analysis breakdown and recommended exercises tailored to your posture pattern.'}
          </p>
        </div>
      )}

      {(isPdf || detailsUnlocked) && classification && (
        <>
          <div className={cn(styles.card, isPdf && 'page-break-before')}>
            <div className={styles.cardTitle}>Analysis Details</div>
            <p className={styles.analysisHint}>
              Rule-driven signals detected from your assessment photos.
            </p>
            <div className={styles.nestedWell}>
              <ClassificationDetails
                classification={classification}
                showDetailSections={!isPdf}
                laymanMode={true}
              />
            </div>
          </div>
          {isPdf && (
            <div className={styles.disclaimerWrap}>
              <p className={styles.disclaimer}>
                <strong>Disclaimer:</strong> {disclaimer}
              </p>
            </div>
          )}
        </>
      )}

      {/* 6. Download Report & Lead Gate Section */}
      {!isPdf && downloadSection}
      {!isPdf && !detailsUnlocked && leadGateSection}

      {/* 7. Recommended Exercises Section (unlocked after report download) */}
      {!shouldHideExercises && (isPdf || detailsUnlocked) && drills.length > 0 && (
        <div className={cn(styles.card, isPdf && 'page-break-before')}>
          <div className="text-center mb-4">
            <h2 className={`${styles.cardTitle} justify-center`}>Recommended Exercises</h2>
            <p className={styles.exercisesIntro}>{exercisesIntro}</p>
          </div>

          <div className="flex flex-col gap-8">
            {drills.map((drill, i) => (
              <div key={`${drill.name}-${i}`} className={styles.drillCard}>
                <div className="flex flex-col items-center gap-2">
                  <div className={cn('p-1.5 rounded-full', styles.drillCheck)} style={drillCheckStyle}>
                    <CheckCircle2 className="h-5 w-5" />
                  </div>
                  <h3 className={styles.drillName}>
                    {i + 1}. {drill.name}
                  </h3>
                  <p className={styles.drillDesc}>{drill.description}</p>
                  {drill.videoUrl && (
                    <a
                      href={drill.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.videoLink}
                      style={linkStyle}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Watch Tutorial Video ({drill.name})
                    </a>
                  )}
                </div>
                <DrillVideoBlock drill={drill} isPdf={isPdf} followAppTheme={useAppTheme} />
              </div>
            ))}
          </div>
        </div>
      )}

      {!isPdf && (
        <div className={styles.screenDisclaimerWrap}>
          <p className={styles.disclaimer}>
            <strong>Disclaimer:</strong> {disclaimer}
          </p>
        </div>
      )}
    </div>
  );
};

export default SelfAssessmentReport;
